/**
 * AppI18nProvider - Web Component for providing translations to child components
 *
 * Usage:
 * <app-i18n-provider translations='{"foo": {"bar": "hello"}}'>
 *   <app-i18n-t key="foo.bar"></app-i18n-t>
 * </app-i18n-provider>
 */

export class AppI18nProvider extends HTMLElement {
  static observedAttributes = ['translations']
  #translations: Record<string, any> = {}

  constructor() {
    super()
  }

  connectedCallback() {
    this.#parseTranslations()
  }

  attributeChangedCallback(name: string, _oldValue: string, _newValue: string) {
    if (name === 'translations') {
      this.#parseTranslations()
      this.#notifyChildren()
    }
  }

  #parseTranslations() {
    const translationsAttr = this.getAttribute('translations')
    if (!translationsAttr) {
      console.warn('[AppI18nProvider] No translations attribute provided')
      this.#translations = {}
      return
    }

    try {
      this.#translations = JSON.parse(translationsAttr)
    } catch (error) {
      console.error('[AppI18nProvider] Failed to parse translations JSON', error)
      this.#translations = {}
    }
  }

  #notifyChildren() {
    // Notify all child app-i18n-t components to re-render
    const children = this.querySelectorAll('app-i18n-t')
    children.forEach((child) => {
      if (child instanceof HTMLElement && typeof (child as any).refresh === 'function') {
        ;(child as any).refresh()
      }
    })
  }

  /**
   * Get a translation by key, with optional variable substitution
   * @param key - Dot-notation key (e.g., "questions.when_violation")
   * @param vars - Variables for interpolation (e.g., {current: 2, total: 5})
   * @returns Translated text or the key if not found
   */
  public getTranslation(key: string, vars?: Record<string, any>): string {
    if (!key) {
      console.warn('[AppI18nProvider] Empty translation key provided')
      return ''
    }

    // Navigate nested object using dot notation
    let text: string = this.#getNestedValue(this.#translations, key) || ''

    if (!text) {
      console.warn(`[AppI18nProvider] Translation missing for key: ${key}`)
      return key
    }

    // Perform variable substitution (e.g., "%{current}" -> "2")
    if (vars) {
      Object.entries(vars).forEach(([varKey, value]) => {
        text = text.replace(new RegExp(`%\\{${varKey}\\}`, 'g'), String(value))
      })
    }

    return text
  }

  #getNestedValue(obj: any, path: string): string | undefined {
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  /**
   * Check if translations are loaded
   */
  public hasTranslations(): boolean {
    return Object.keys(this.#translations).length > 0
  }

  disconnectedCallback() {
    // Cleanup if needed
  }
}

customElements.define('app-i18n-provider', AppI18nProvider)
